<template>
  <div>
    <v-skeleton-loader v-if="carregando" type="article" />
    <v-alert
      v-else-if="erro"
      type="error"
      color="red darken-2"
      outlined
      dense
      >{{ erro }}</v-alert
    >
    <template v-else>
      <div class="text-caption text--secondary mb-2">
        Como o INSS do empregado é descontado no cálculo da folha. Aposentado
        que trabalha contribui normalmente (Lei 8.212/1991, art. 12, § 4º):
        mantenha a retenção padrão. IRRF e FGTS são sempre calculados por
        vínculo e não mudam com esta opção.
      </div>

      <v-radio-group v-model="form.tipo" :disabled="!podeEditar" class="mt-0">
        <v-radio v-for="t in tipos" :key="t.value" :value="t.value">
          <template v-slot:label>
            <div>
              <div class="font-weight-bold text-body-2">{{ t.text }}</div>
              <div class="text-caption text--secondary">{{ t.descricao }}</div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>

      <template v-if="multiplos">
        <v-alert
          type="info"
          color="blue darken-2"
          text
          dense
          class="text-body-2"
        >
          Informe só as empresas que descontam o INSS <strong>antes</strong>
          desta, conforme a declaração do empregado (IN RFB 2.110/2022, art. 36
          e Anexo VIII). A declaração é mensal; pode abranger várias
          competências do mesmo ano e deve ser renovada a cada ano. Se esta
          empresa é a primeira a descontar, use a retenção padrão. Remuneração
          de regime próprio (RPPS) não entra.
        </v-alert>

        <v-card
          v-for="(v, i) in form.outrosVinculos"
          :key="v.chave"
          outlined
          class="pa-3 mb-3"
        >
          <div class="d-flex align-center mb-2">
            <strong class="text-body-2">Outra empresa {{ i + 1 }}</strong>
            <v-spacer />
            <v-btn
              v-if="podeEditar"
              icon
              small
              title="Remover"
              @click="remover(i)"
            >
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
          <v-row dense>
            <v-col cols="12" md="2">
              <span class="opacity-5 text-dark">Tipo</span>
              <v-select
                v-model="v.tipoInscricao"
                :items="tiposInscricao"
                label="Tipo"
                :disabled="!podeEditar"
                :class="campo"
                dense
                flat
                filled
                solo
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <span class="opacity-5 text-dark">{{
                v.tipoInscricao === 2 ? "CPF" : "CNPJ"
              }}</span>
              <v-text-field
                v-model="v.inscricao"
                :label="v.tipoInscricao === 2 ? 'CPF' : 'CNPJ'"
                maxlength="18"
                :disabled="!podeEditar"
                :class="campo"
                dense
                flat
                filled
                solo
                hide-details
              />
            </v-col>
            <v-col cols="12" md="7">
              <span class="opacity-5 text-dark"
                >Nome empresarial ou do empregador</span
              >
              <v-text-field
                v-model="v.nome"
                label="Nome"
                maxlength="150"
                :disabled="!podeEditar"
                :class="campo"
                dense
                flat
                filled
                solo
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <span class="opacity-5 text-dark"
                >Categoria na outra empresa (Tabela 01)</span
              >
              <v-select
                v-model="v.categoria"
                :items="categorias"
                label="Categoria"
                hint="Contribuinte individual conta só para o teto."
                persistent-hint
                :disabled="!podeEditar"
                :class="campo"
                dense
                flat
                filled
                solo
              />
            </v-col>
            <v-col cols="6" md="3">
              <span class="opacity-5 text-dark">A partir de</span>
              <CampoCompetencia
                :payload="v.competenciaInicio"
                label="MM/AAAA"
                :disabled="!podeEditar"
                @date="v.competenciaInicio = $event"
              />
            </v-col>
            <v-col cols="6" md="3">
              <span class="opacity-5 text-dark">Até</span>
              <CampoCompetencia
                :payload="v.competenciaFim"
                label="MM/AAAA"
                opcional
                :disabled="!podeEditar"
                @date="v.competenciaFim = $event"
              />
              <small class="folha-dica"
                >Em branco: dezembro do mesmo ano.</small
              >
            </v-col>
            <v-col cols="12" md="6">
              <span class="opacity-5 text-dark"
                >Remuneração mensal na outra empresa</span
              >
              <CampoValorFolha
                v-model="v.remuneracao"
                label="Remuneração"
                :disabled="!podeEditar"
                dica="Valor sobre o qual a outra empresa desconta o INSS."
              />
            </v-col>
            <v-col cols="12" md="6">
              <span class="opacity-5 text-dark"
                >13º salário na outra empresa (opcional)</span
              >
              <CampoValorFolha
                v-model="v.remuneracaoDecimoTerceiro"
                label="13º salário"
                :disabled="!podeEditar"
                dica="Usado no INSS do 13º quando a outra empresa paga antes."
              />
            </v-col>
          </v-row>
        </v-card>

        <div
          v-if="!form.outrosVinculos.length"
          class="text-body-2 text--secondary mb-2"
        >
          Nenhuma empresa informada: o INSS será calculado desde a 1ª faixa, com
          alerta no demonstrativo.
        </div>
        <v-btn
          v-if="podeEditar"
          text
          color="blue darken-2"
          class="text-none px-0"
          @click="adicionar"
        >
          <v-icon left>mdi-plus</v-icon> Adicionar empresa
        </v-btn>
      </template>

      <div class="d-flex align-center mt-3">
        <span v-if="dtAlteracao" class="text-caption text--secondary">
          Alterado em {{ data(dtAlteracao) }}
        </span>
        <v-spacer />
        <v-btn
          v-if="podeEditar"
          :loading="salvando"
          :class="btnPrimario"
          height="39"
          @click="salvar"
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          Salvar retenção do INSS
        </v-btn>
      </div>
    </template>
  </div>
</template>
<script>
import FolhaCalculoDataService from "@/services/pessoal/FolhaCalculoDataService";
import folhaUi from "./folhaUi";
import CampoValorFolha from "./CampoValorFolha.vue";

const TIPOS = [
  {
    value: "PADRAO",
    text: "Retenção padrão",
    descricao:
      "Vínculo único, ou esta empresa é a primeira a descontar: tabela progressiva desde a 1ª faixa, até o teto.",
  },
  {
    value: "MULTIPLOS_VINCULOS",
    text: "Múltiplos vínculos",
    descricao:
      "Outras empresas descontam antes desta: o INSS continua nas faixas seguintes e para no teto; com o teto atingido nelas, não há desconto.",
  },
];

// Tabela 01 do eSocial: categorias com remuneração no RGPS (mesma lista aceita pela API).
const CATEGORIAS = [
  { header: "Empregado" },
  { value: 101, text: "101 - Empregado - Geral" },
  { value: 102, text: "102 - Empregado - Rural por pequeno prazo" },
  { value: 103, text: "103 - Empregado - Aprendiz" },
  { value: 104, text: "104 - Empregado - Doméstico" },
  { value: 105, text: "105 - Empregado - Contrato a termo (Lei 9.601/1998)" },
  { value: 106, text: "106 - Trabalhador temporário (Lei 6.019/1974)" },
  { value: 111, text: "111 - Empregado - Contrato intermitente" },
  { header: "Avulso" },
  { value: 201, text: "201 - Trabalhador avulso portuário" },
  { value: 202, text: "202 - Trabalhador avulso não portuário" },
  { header: "Agente público (somente se vinculado ao RGPS)" },
  { value: 301, text: "301 - Servidor titular de cargo efetivo" },
  { value: 302, text: "302 - Servidor ocupante de cargo em comissão" },
  { value: 303, text: "303 - Exercente de mandato eletivo" },
  { value: 304, text: "304 - Servidor exercente de mandato eletivo" },
  {
    value: 305,
    text: "305 - Servidor indicado para conselho ou órgão deliberativo",
  },
  { value: 306, text: "306 - Servidor contratado por tempo determinado" },
  { value: 307, text: "307 - Militar dos Estados e Distrito Federal" },
  { value: 309, text: "309 - Agente público - Outros" },
  { value: 310, text: "310 - Servidor público eventual" },
  { value: 311, text: "311 - À disposição da Justiça Eleitoral" },
  { value: 312, text: "312 - Auxiliar local" },
  {
    value: 313,
    text: "313 - Servidor em instrutoria, curso, concurso ou parecer",
  },
  { value: 314, text: "314 - Militar das Forças Armadas" },
  { header: "Contribuinte individual (conta só para o teto)" },
  { value: 701, text: "701 - Autônomo em geral" },
  { value: 711, text: "711 - Transportador autônomo de passageiros" },
  { value: 712, text: "712 - Transportador autônomo de carga" },
  { value: 721, text: "721 - Diretor não empregado, com FGTS" },
  { value: 722, text: "722 - Diretor não empregado, sem FGTS" },
  { value: 723, text: "723 - Empresário, sócio ou membro de conselho" },
  { value: 731, text: "731 - Cooperado de cooperativa de trabalho" },
  { value: 734, text: "734 - Transportador cooperado" },
  { value: 738, text: "738 - Cooperado filiado a cooperativa de produção" },
  { value: 741, text: "741 - Microempreendedor individual" },
  { value: 751, text: "751 - Magistrado classista temporário aposentado" },
  { value: 761, text: "761 - Associado eleito para direção de entidade" },
  { value: 771, text: "771 - Membro de conselho tutelar" },
  { value: 781, text: "781 - Ministro de confissão religiosa" },
];

let sequencia = 0;

export default {
  name: "RetencaoInssFuncionario",
  components: { CampoValorFolha },
  mixins: [folhaUi],
  props: { funcionarioId: { type: Number, default: null } },
  data() {
    return {
      carregando: false,
      salvando: false,
      erro: null,
      dtAlteracao: null,
      salvoComEmpresas: false,
      form: { tipo: "PADRAO", outrosVinculos: [] },
      tipos: TIPOS,
      categorias: CATEGORIAS,
      tiposInscricao: [
        { value: 1, text: "CNPJ" },
        { value: 2, text: "CPF" },
      ],
    };
  },
  computed: {
    podeEditar() {
      return this.hasPermission("ROLE_Funcionario_Update");
    },
    multiplos() {
      return this.form.tipo === "MULTIPLOS_VINCULOS";
    },
  },
  watch: {
    funcionarioId: { handler: "carregar", immediate: true },
  },
  methods: {
    aplicar(dto) {
      this.dtAlteracao = dto.dtAlteracao || null;
      this.salvoComEmpresas = (dto.outrosVinculos || []).length > 0;
      this.form = {
        tipo: dto.tipo || "PADRAO",
        outrosVinculos: (dto.outrosVinculos || []).map((v) => ({
          ...v,
          chave: ++sequencia,
        })),
      };
    },
    async carregar() {
      if (!this.funcionarioId) return;
      this.carregando = true;
      this.erro = null;
      try {
        const { data } = await FolhaCalculoDataService.getRetencaoInss(
          this.funcionarioId
        );
        this.aplicar(data);
      } catch (error) {
        this.erro =
          error?.response?.data?.message ||
          "Não foi possível carregar a retenção do INSS do funcionário.";
      } finally {
        this.carregando = false;
      }
    },
    adicionar() {
      this.form.outrosVinculos.push({
        chave: ++sequencia,
        id: null,
        tipoInscricao: 1,
        inscricao: "",
        nome: "",
        categoria: 101,
        competenciaInicio: this.primeiroDiaMes(this.hoje()),
        competenciaFim: null,
        remuneracao: null,
        remuneracaoDecimoTerceiro: null,
      });
    },
    remover(i) {
      this.form.outrosVinculos.splice(i, 1);
    },
    valor(v) {
      return v === "" || v === null || v === undefined ? null : Number(v);
    },
    async salvar() {
      if (
        !this.multiplos &&
        this.salvoComEmpresas &&
        !(await this.confirmar(
          "Voltar à retenção padrão?",
          "As empresas informadas serão removidas e os próximos cálculos descontarão o INSS desde a 1ª faixa.",
          "Salvar"
        ))
      )
        return;
      this.salvando = true;
      try {
        const payload = {
          tipo: this.form.tipo,
          outrosVinculos: this.multiplos
            ? this.form.outrosVinculos.map((v) => ({
                id: v.id,
                tipoInscricao: v.tipoInscricao,
                inscricao: v.inscricao,
                nome: v.nome,
                categoria: v.categoria,
                competenciaInicio: this.competenciaApi(v.competenciaInicio),
                competenciaFim: this.competenciaApi(v.competenciaFim),
                remuneracao: this.valor(v.remuneracao),
                remuneracaoDecimoTerceiro: this.valor(
                  v.remuneracaoDecimoTerceiro
                ),
              }))
            : [],
        };
        const { data } = await FolhaCalculoDataService.salvarRetencaoInss(
          this.funcionarioId,
          payload
        );
        this.aplicar(data);
        this.$emit("salvo");
        this.$swal.fire(
          "Sucesso",
          "Retenção do INSS salva. Recalcule os demonstrativos abertos do funcionário.",
          "success"
        );
      } catch (error) {
        this.mostrarErro(error, "Falha ao salvar a retenção do INSS.");
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>
